"use client";

import {
  OBSERVED_SEASON_SHIFT,
  PROJECTED_SEASON_SHIFTS,
  RELIABILITY_PERIODS,
  SEASON_SOURCES,
  SNOW_DAY_PERIODS,
  SNOW_DAY_STATIONS,
  SNOW_MASSIFS,
  SNOW_SCENARIOS,
  snowDayComparison,
  snowReliabilitySeries,
  type SnowDayPeriod,
  type SnowDayStationId,
  type SnowMassifId,
  type SnowScenario,
} from "@/lib/climate/season-evidence";
import { DataTable, Segments } from "./ui";
import { useClimateState } from "./use-climate-state";
import { ShareTools } from "./share-tools";
import s from "./climate.module.css";
import c from "./changing-seasons.module.css";

const ALTITUDES = [
  "1000",
  "1500",
  "1800",
  "2000",
  "2500",
  "3000",
  "3500",
] as const;
const xPositions = ["12%", "50%", "88%"];
const formatMetres = (value: number) => `${value.toLocaleString("en-GB")} m`;
const elevationY = (value: number) => 250 - ((value - 1000) / 2500) * 250;

function SourceLink({ source }: { source: keyof typeof SEASON_SOURCES }) {
  const entry = SEASON_SOURCES[source];
  return (
    <a className={c.source} href={entry.url} target="_blank" rel="noreferrer">
      {entry.label} ↗
    </a>
  );
}

function SnowElevation() {
  const [massifId, setMassifId] = useClimateState<SnowMassifId>(
    "snowMassif",
    "chablais",
    SNOW_MASSIFS.map((row) => row.id),
  );
  const [scenario, setScenario] = useClimateState<SnowScenario>(
    "snowScenario",
    "rcp45",
    SNOW_SCENARIOS.map((row) => row.value),
  );
  const [altitude, setAltitude] = useClimateState<(typeof ALTITUDES)[number]>(
    "snowAltitude",
    "1800",
    ALTITUDES,
  );
  const massif = SNOW_MASSIFS.find((row) => row.id === massifId)!;
  const selectedScenario = SNOW_SCENARIOS.find(
    (row) => row.value === scenario,
  )!;
  const series = snowReliabilitySeries(massif, scenario);
  const rise = series[2].elevationM - massif.reference;
  return (
    <section
      className={s.section}
      id="snow-elevation"
      aria-labelledby="snow-elevation-title"
    >
      <div className={s.wrap}>
        <div className={c.heading}>
          <div>
            <div className={s.eyebrow}>Snow · Elevation and time</div>
            <h2 id="snow-elevation-title">Snow reliability climbs uphill.</h2>
            <p>
              Higher terrain keeps an advantage, but the height needed for
              dependable snow changes with place and emissions. Explore eight
              published Alpine and Pyrenean case studies.
            </p>
          </div>
          <span className={c.evidenceTag}>Model projections · 2019 study</span>
        </div>
        <article className={s.panel}>
          <div className={c.controls}>
            <label className={s.field}>
              Mountain massif
              <select
                value={massifId}
                onChange={(event) =>
                  setMassifId(event.target.value as SnowMassifId)
                }
              >
                {SNOW_MASSIFS.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name}
                  </option>
                ))}
              </select>
            </label>
            <label className={s.field}>
              Compare a terrain elevation
              <select
                value={altitude}
                onChange={(event) =>
                  setAltitude(event.target.value as (typeof ALTITUDES)[number])
                }
              >
                {ALTITUDES.map((value) => (
                  <option key={value} value={value}>
                    {formatMetres(Number(value))}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <Segments
            label="Snow reliability emissions pathway"
            options={SNOW_SCENARIOS}
            value={scenario}
            onChange={setScenario}
          />
          <div className={c.elevationLayout}>
            <figure
              className={c.elevationFigure}
              aria-labelledby="elevation-caption"
            >
              <div className={c.axisTitle}>
                <span>Elevation above sea level</span>
                <span className={c.terrainKey}>
                  Your terrain: {formatMetres(Number(altitude))}
                </span>
              </div>
              <div className={c.plotGrid}>
                <div className={c.yAxis} aria-hidden="true">
                  {[3500, 3000, 2500, 2000, 1500, 1000].map((tick) => (
                    <span
                      key={tick}
                      style={{ top: `${(elevationY(tick) / 250) * 100}%` }}
                    >
                      {tick.toLocaleString("en-GB")}
                    </span>
                  ))}
                </div>
                <svg
                  className={c.elevationPlot}
                  width="100%"
                  height="250"
                  role="img"
                  aria-label={`${massif.name}, ${selectedScenario.label}: reliability elevation ${series.map((row) => `${row.label}: ${row.elevationM} metres`).join(", ")}. Comparison terrain: ${altitude} metres.`}
                >
                  {[3500, 3000, 2500, 2000, 1500, 1000].map((tick) => (
                    <line
                      key={tick}
                      x1="0"
                      x2="100%"
                      y1={elevationY(tick)}
                      y2={elevationY(tick)}
                      className={c.gridLine}
                    />
                  ))}
                  <line
                    x1="0"
                    x2="100%"
                    y1={elevationY(Number(altitude))}
                    y2={elevationY(Number(altitude))}
                    className={c.terrainLine}
                  />
                  {SNOW_SCENARIOS.map((pathway) => {
                    const values = snowReliabilitySeries(massif, pathway.value);
                    return (
                      <g
                        key={pathway.value}
                        opacity={pathway.value === scenario ? 1 : 0.35}
                      >
                        {[0, 1].map((index) => (
                          <line
                            key={index}
                            x1={xPositions[index]}
                            x2={xPositions[index + 1]}
                            y1={elevationY(values[index].elevationM)}
                            y2={elevationY(values[index + 1].elevationM)}
                            stroke={pathway.colour}
                            strokeWidth={pathway.value === scenario ? 3 : 2}
                            strokeDasharray="5 6"
                          />
                        ))}
                        {values.map((row, index) => (
                          <circle
                            key={row.key}
                            cx={xPositions[index]}
                            cy={elevationY(row.elevationM)}
                            r={pathway.value === scenario ? 6 : 4}
                            fill={pathway.colour}
                            stroke="white"
                            strokeWidth="2"
                          />
                        ))}
                      </g>
                    );
                  })}
                  {series.map((row, index) => (
                    <text
                      key={row.key}
                      x={xPositions[index]}
                      y={Math.max(14, elevationY(row.elevationM) - 14)}
                      textAnchor={index === 0 ? "start" : index === 2 ? "end" : "middle"}
                      className={c.pointLabel}
                      aria-hidden="true"
                    >
                      {formatMetres(row.elevationM)}
                    </text>
                  ))}
                </svg>
              </div>
              <div className={c.xAxis} aria-hidden="true">
                {RELIABILITY_PERIODS.map((period) => (
                  <span key={period.key}>{period.label}</span>
                ))}
              </div>
              <figcaption id="elevation-caption" className={c.chartCaption}>
                Coloured points: the study’s reliability threshold. Black dashed
                line: your terrain height. Joining lines guide the eye between
                published periods; they do not predict individual years.
              </figcaption>
              <p className={c.terrainTakeaway} aria-live="polite">
                <strong>At {formatMetres(Number(altitude))}:</strong> your chosen
                terrain sits {Number(altitude) < series[1].elevationM ? "below" : Number(altitude) > series[1].elevationM ? "above" : "at"} the
                modelled 2030–2050 reliability threshold in {massif.name} under{" "}
                {selectedScenario.label.split(" · ")[1]}. Compare the height of
                the runs and connecting slopes, as well as the resort village.
              </p>
            </figure>
            <div className={c.elevationReadout} aria-live="polite">
              <span className={c.kicker}>{massif.region}</span>
              <h3>{massif.name}</h3>
              <p className={c.bigStat}>
                +{rise.toLocaleString("en-GB")}
                <span>m</span>
              </p>
              <p className={s.small}>
                Rise in the modelled reliability elevation by 2080–2100 under{" "}
                {selectedScenario.label.split(" · ")[1]}, relative to 1986–2005.
              </p>
              <div className={c.thresholdList}>
                {series.map((row) => (
                  <div key={row.key}>
                    <span>{row.label}</span>
                    <strong>{formatMetres(row.elevationM)}</strong>
                    <small>
                      {formatMetres(Number(altitude))} is{" "}
                      {Number(altitude) < row.elevationM
                        ? "below"
                        : Number(altitude) > row.elevationM
                          ? "above"
                          : "at"}{" "}
                      this threshold
                    </small>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <p className={s.note}>
            <strong>What “reliable” means here:</strong> at least 100 days with
            100 kg/m² of groomed natural snow between 15 December and 15 April,
            reached in 7 winters out of 10 in the model. No snowmaking. This is
            a massif-scale benchmark, not a prediction for a particular run or
            resort’s opening days.
          </p>
          <DataTable
            headers={["Pathway", "1986–2005 HIST", "2030–2050", "2080–2100"]}
            rows={SNOW_SCENARIOS.map((row) => [
              row.label,
              ...snowReliabilitySeries(massif, row.value).map((point) =>
                formatMetres(point.elevationM),
              ),
            ])}
          />
          <ShareTools chart="snow-elevation" />
          <div className={c.sources}>
            <SourceLink source="reliability" />
            <span>Appendix A1–A3 · 70% reliability columns</span>
          </div>
          <p className={c.caveat}>
            The reference is the historical climate-model ensemble (HIST), not
            direct station observations. The 70% criterion describes frequency
            across modelled winters, not confidence in a projection. RCP
            pathways are separate experiments from the SSP pathways elsewhere on
            this page.
          </p>
        </article>
      </div>
    </section>
  );
}

function TimingBars({
  onset,
  melt,
  unit,
  max,
}: {
  onset: number;
  melt: number;
  unit: "days" | "weeks";
  max: number;
}) {
  return (
    <div className={c.timingBars}>
      <div className={c.timingRow}>
        <div>
          <span>Snow arrives later</span>
          <strong>
            +{onset} {unit}
          </strong>
        </div>
        <div className={c.timingRail}>
          <span style={{ width: `${(onset / max) * 100}%` }} />
          <b aria-hidden="true">→</b>
        </div>
      </div>
      <div className={c.timingRow}>
        <div>
          <span>Snow disappears earlier</span>
          <strong>
            −{melt} {unit}
          </strong>
        </div>
        <div className={`${c.timingRail} ${c.meltRail}`}>
          <span style={{ width: `${(melt / max) * 100}%` }} />
          <b aria-hidden="true">←</b>
        </div>
      </div>
    </div>
  );
}

function SnowSeasonLength() {
  const [stationId, setStationId] = useClimateState<SnowDayStationId>(
    "seasonStation",
    "davos",
    SNOW_DAY_STATIONS.map((row) => row.id),
  );
  const [period, setPeriod] = useClimateState<
    Exclude<SnowDayPeriod, "reference">
  >("seasonPeriod", "late", ["near", "middle", "late"]);
  const [timingPeriod, setTimingPeriod] = useClimateState<
    keyof typeof PROJECTED_SEASON_SHIFTS
  >("seasonTiming", "late", ["near", "late"]);
  const station = SNOW_DAY_STATIONS.find((row) => row.id === stationId)!;
  const comparison = snowDayComparison(station, period);
  const selectedPeriod = SNOW_DAY_PERIODS.find((row) => row.key === period)!;
  const timing = PROJECTED_SEASON_SHIFTS[timingPeriod];
  return (
    <section
      className={s.section}
      id="season-length"
      aria-labelledby="season-length-title"
    >
      <div className={s.wrap}>
        <div className={c.heading}>
          <div>
            <div className={s.eyebrow}>Snow · The shape of a season</div>
            <h2 id="season-length-title">Winter loses its edges.</h2>
            <p>
              Swiss observations show snow arriving later and disappearing
              earlier. Projections point to fewer snow-covered days, with large
              differences between places. Explore the duration evidence and the
              timing evidence separately.
            </p>
          </div>
          <span className={c.evidenceTag}>
            Observations + model projections
          </span>
        </div>
        <article className={s.panel}>
          <div className={c.durationHeading}>
            <div>
              <span className={c.kicker}>
                Projected snow-day counts · SRES A2
              </span>
              <h3>How many days hold snow?</h3>
              <p className={c.interactionHint}>Select a future period below to compare it with the modelled reference.</p>
            </div>
            <label className={s.field}>
              Swiss model location
              <select
                value={stationId}
                onChange={(event) =>
                  setStationId(event.target.value as SnowDayStationId)
                }
              >
                {SNOW_DAY_STATIONS.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className={c.durationLayout}>
            <figure aria-labelledby="snow-days-caption">
              <div className={c.durationBars}>
                {SNOW_DAY_PERIODS.map((row) => {
                  const value = station.days[row.key];
                  const contents = (
                    <>
                      <span className={c.durationLabel}>
                        <strong>{row.label}</strong>
                        <span>{row.name}</span>
                      </span>
                      <span className={c.durationTrack} aria-hidden="true">
                        <span
                          style={{ width: `${(value / 300) * 100}%` }}
                          data-reference={row.key === "reference"}
                        />
                      </span>
                      <span className={c.durationNumber}>
                        {value}
                        <small>days</small>
                      </span>
                    </>
                  );
                  return row.key === "reference" ? (
                    <div className={c.durationRow} key={row.key}>
                      {contents}
                    </div>
                  ) : (
                    <button
                      type="button"
                      className={c.durationRow}
                      key={row.key}
                      aria-pressed={row.key === period}
                      onClick={() =>
                        setPeriod(
                          row.key as Exclude<SnowDayPeriod, "reference">,
                        )
                      }
                      aria-label={`Compare ${row.label}: ${value} snow days at ${station.name}`}
                    >
                      {contents}
                    </button>
                  );
                })}
              </div>
              <div className={c.durationAxis} aria-hidden="true">
                {[0, 100, 200, 300].map((tick) => (
                  <span key={tick}>{tick}</span>
                ))}
              </div>
              <figcaption id="snow-days-caption" className={c.chartCaption}>
                Mean days per year with at least 5 cm of snow. All bars start at
                zero for length comparison. They do not show calendar dates or a
                continuous season.
              </figcaption>
            </figure>
            <div className={c.durationReadout} aria-live="polite">
              <span className={c.kicker}>
                {station.name} · {selectedPeriod.label}
              </span>
              <p className={c.bigStat}>
                −{comparison.lostDays}
                <span>days</span>
              </p>
              <p className={s.small}>
                {comparison.days} snow-covered days compared with{" "}
                {comparison.reference} in the modelled 1999–2012 reference.
                About {comparison.retainedPercent}% of that reference count
                remains.
              </p>
              <p className={c.caveat}>
                A 5 cm snow cover is not a skiable piste. These counts do not
                include snowmaking or predict resort operating dates.
              </p>
            </div>
          </div>
          <DataTable
            headers={[
              "Location",
              "1999–2012",
              "2020–2049",
              "2045–2074",
              "2070–2099",
            ]}
            rows={SNOW_DAY_STATIONS.map((row) => [
              row.name,
              ...SNOW_DAY_PERIODS.map((entry) => row.days[entry.key]),
            ])}
          />
          <ShareTools chart="season-length" />
          <div className={c.sources}>
            <SourceLink source="snowDays" />
            <span>Table S2 · all values in days</span>
          </div>
          <p className={c.caveat}>
            The chart follows Table S2’s label, “mean number of snow days”. The
            article’s main text calls these medians; the published numbers are
            reproduced unchanged.
          </p>
          <p className={s.note}>
            This 2017 study uses the older SRES A2 scenario, a high-emissions
            experiment. It is not a current-policy forecast and cannot be
            relabelled as one of the SSP pathways above. Its reference snow
            cover is modelled from 1999–2012 weather. Individual years vary; the
            table does not supply uncertainty intervals.
          </p>
        </article>
        <div className={c.timingGrid}>
          <article className={s.panel}>
            <span className={c.kicker}>Observed · 1970 to 2015</span>
            <h3>Spring changed more than autumn.</h3>
            <p className={s.small}>
              Average shifts across {OBSERVED_SEASON_SHIFT.stations} Swiss
              Alpine stations between 1,139 and 2,540 m.
            </p>
            <TimingBars
              onset={OBSERVED_SEASON_SHIFT.onsetLaterDays}
              melt={OBSERVED_SEASON_SHIFT.meltEarlierDays}
              unit="days"
              max={30}
            />
            <p className={s.small}>
              Snow-cover duration shortened at every study station, averaging
              8.9 days per decade. The later-onset trend was statistically
              significant at about 30% of stations.
            </p>
            <SourceLink source="observedTiming" />
          </article>
          <article className={s.panel}>
            <span className={c.kicker}>
              Projected · Aare catchment at 1,500 m
            </span>
            <h3>The season contracts from both ends.</h3>
            <Segments
              label="Snow-season timing period"
              value={timingPeriod}
              onChange={setTimingPeriod}
              options={[
                { value: "near", label: "2020–2049" },
                { value: "late", label: "2070–2099" },
              ]}
            />
            <div aria-live="polite">
              <TimingBars
                onset={timing.onsetLaterWeeks}
                melt={timing.meltEarlierWeeks}
                unit="weeks"
                max={12}
              />
            </div>
            <p className={s.small}>
              Approximate shifts from the modelled 1999–2012 reference under
              SRES A2. Timing refers to the longest continuous cover of at least
              1 cm. This catchment result does not change with the location
              selected above.
            </p>
            <SourceLink source="futureTiming" />
          </article>
        </div>
        <p className={c.caveat}>
          The timing cards use different populations, periods and units:
          observed days on the left, projected weeks on the right. They are not
          a single time series. No future opening or closing date has been
          inferred from a snow-day count.
        </p>
      </div>
    </section>
  );
}

export function ChangingSeasons() {
  return (
    <>
      <SnowElevation />
      <SnowSeasonLength />
    </>
  );
}
